import * as React from "react"
import { useState, useEffect, useCallback } from "react"
import {
    Box, Typography, IconButton, Button, Chip, CircularProgress
} from "@mui/material"
import { IconSeeker, ImageSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit, postSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { imageComparisonModal } from "../@signals"

const RP = BACKEND_APIS.RETAILER_PRODUCTS
const memberKey = (m) => `${m.id_site}|${m.product_id}`
const fmtPrice = (p, c) => (p == null ? null : `${p.toLocaleString()}${c ? ` ${c}` : ''}`)

// One product row (member or candidate) with image, name, site, price.
function ProductRow({ item, anchor, action }) {
    const image = item.image || item.image_url
    return (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', py: 1 }}>
            <ImageSeeker
                src={image}
                sx={{ width: 56, height: 56, borderRadius: 1, border: '1px solid #e5e7eb',
                      objectFit: 'contain', backgroundColor: '#fff', cursor: image ? 'zoom-in' : 'default',
                      '&:hover': { opacity: image ? 0.85 : 1 } }}
                onClick={() => image && (imageComparisonModal.value = {
                    title: anchor?.title,
                    rawImage: anchor?.image,
                    leftLabel: 'This product',
                    skuImage: image,
                    rightLabel: item.site_name || 'Match',
                    skuName: item.name,
                })}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }} noWrap title={item.name}>
                    {item.product_url ? (
                        <Box component="a" href={item.product_url} target="_blank" rel="noopener noreferrer"
                             onClick={(e) => e.stopPropagation()}
                             sx={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0, '&:hover': { textDecoration: 'underline' } }}>
                            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name || `(${item.product_id})`}</Box>
                            <IconSeeker icon="external-link" sx={{ fontSize: '0.75rem', color: 'text.disabled', flexShrink: 0 }} />
                        </Box>
                    ) : (item.name || `(${item.product_id})`)}
                </Typography>
                {item.subtitle && (
                    <Typography variant="caption" color="text.secondary" noWrap display="block">
                        {item.subtitle}
                    </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip size="small" variant="outlined" label={item.site_name || `Site ${item.id_site}`}
                          sx={{ height: 18, fontSize: '0.65rem' }} />
                    {item.country_code && (
                        <Typography variant="caption" color="text.secondary">{item.country_code}</Typography>
                    )}
                    {item.variant_raw && (
                        <Typography variant="caption" color="text.secondary">· {item.variant_raw}</Typography>
                    )}
                    {fmtPrice(item.actual_price, item.currency) && (
                        <Typography variant="caption" color="text.secondary">
                            {fmtPrice(item.actual_price, item.currency)}
                        </Typography>
                    )}
                    {item.is_matched && (
                        <Chip size="small" color="warning" variant="outlined" label="already mapped"
                              sx={{ height: 18, fontSize: '0.6rem' }} />
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', opacity: 0.7 }} noWrap display="block">
                    ID: {item.product_id}
                </Typography>
            </Box>
            {action}
        </Box>
    )
}

export default function Raw2RawEditor({ drawerData, onClose }) {
    const { showToast } = useAppToast()
    const raw = drawerData?.raw
    const idCustomer = drawerData?.idCustomer
    const idMatch = drawerData?.idMatch

    const [members, setMembers] = useState([])
    const [loadingMembers, setLoadingMembers] = useState(false)
    const [busyKey, setBusyKey] = useState(null)         // member being unmapped

    const anchorKey = raw ? `${raw.id_site}|${raw.product_id}` : null

    const loadMembers = useCallback(() => {
        if (!idMatch) { setMembers([]); return }
        setLoadingMembers(true)
        getSubmit(`${RP}/match-group/${idMatch}`)
            .then(res => setMembers(res?.members || []))
            .catch(() => { showToast.error('Could not load the match group'); setMembers([]) })
            .finally(() => setLoadingMembers(false))
    }, [idMatch])

    useEffect(() => { loadMembers() }, [loadMembers])

    const handleUnmap = async (member) => {
        if (!idCustomer) return
        setBusyKey(memberKey(member))
        try {
            await postSubmit(`${RP}/unmap`, {
                id_customer: idCustomer,
                id_site: raw.id_site,
                product_id: raw.product_id,
                target_id_site: member.id_site,
                target_product_id: member.product_id,
            })
            showToast.success('Product unmapped from the group')
            loadMembers()
        } catch (e) {
            showToast.error('Error unmapping product')
        } finally {
            setBusyKey(null)
        }
    }

    const otherMembers = members.filter(m => memberKey(m) !== anchorKey)

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Edit Match</Typography>
                    <Chip size="small" label="Product ↔ Product" sx={{ bgcolor: '#ede9fe', color: '#6d28d9', fontWeight: 600 }} />
                </Box>
                <IconButton onClick={onClose}><IconSeeker icon="xmark" /></IconButton>
            </Box>

            {/* Anchor product */}
            <Box sx={{ p: 2, backgroundColor: '#fef3c7', borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: '#92400e' }}>This product</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <ImageSeeker src={raw?.image} sx={{ width: 90, height: 90, borderRadius: 1, border: '1px solid #e5e7eb', objectFit: 'contain', backgroundColor: '#fff' }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {raw?.product_url ? (
                                <Box component="a" href={raw.product_url} target="_blank" rel="noopener noreferrer"
                                     sx={{ color: 'inherit', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                                    {raw?.title || '-'}
                                    <IconSeeker icon="external-link" sx={{ fontSize: '0.8rem', color: 'text.disabled' }} />
                                </Box>
                            ) : (raw?.title || '-')}
                        </Typography>
                        {raw?.subtitle && <Typography variant="caption" color="text.secondary" display="block">{raw.subtitle}</Typography>}
                        {raw?.variant_raw && <Typography variant="caption" color="text.secondary" display="block">Variant: {raw.variant_raw}</Typography>}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontFamily: 'monospace' }}>
                            ID: {raw?.product_id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Site: {raw?.site} | Brand: {raw?.brand}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {!idCustomer && (
                <Box sx={{ p: 2, color: '#b45309', backgroundColor: '#fffbeb', borderBottom: '1px solid #e5e7eb' }}>
                    <Typography variant="caption">
                        This audit row has no customer reference, so mapping changes are disabled. (Older entries may lack it.)
                    </Typography>
                </Box>
            )}

            {/* Body: current group + remap */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Matched products in this group {idMatch ? `(#${idMatch})` : ''}
                </Typography>
                {loadingMembers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
                ) : !idMatch ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        This product is not in any match group (it may have been unmapped). You can map it below.
                    </Typography>
                ) : otherMembers.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No other products in this group.
                    </Typography>
                ) : (
                    otherMembers.map(m => (
                        <ProductRow
                            key={memberKey(m)}
                            item={m}
                            anchor={raw}
                            action={
                                <Button size="small" color="error" variant="outlined"
                                        startIcon={busyKey === memberKey(m) ? <CircularProgress size={14} /> : <IconSeeker icon="link-slash" />}
                                        disabled={!idCustomer || busyKey === memberKey(m)}
                                        onClick={() => handleUnmap(m)}>
                                    Unmap
                                </Button>
                            }
                        />
                    ))
                )}
            </Box>

            {/* Footer */}
            <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="outlined" onClick={onClose}>Close</Button>
            </Box>
        </Box>
    )
}
